// Module 10 — the node-asset applier: recolours the static grid-node point primitives from the 10.6
// encoder's AssetSpec. Nodes are built ONCE at mount (coords never change); each tick only flips `lit`
// (node in the live set vs de-energized), so this mutates the per-point COLOUR in place — no primitive
// rebuild (same discipline as walls/plants). A powered asset shows the neutral asset hue at full
// opacity; a de-energized one reads dark + faint — the vision's "go dark on power loss" at node level.
// Class identity is the glyph SHAPE (a future billboard refinement); hue here stays neutral so the
// outage signal rides value + alpha, never colour alone. Boundary: same-module + domain only.
import type { AssetSpec } from "./effects/spec";
import { assetConditionStyle } from "./effects/palette";
import type { CesiumLike, PointPrimitiveLike, ColorLike } from "./cesium-api";

export interface NodeAssetStyle {
  hex: string;
  alpha: number;
}

// P14b: a node point recolours by its service CONDITION, not just lit/unlit. A de-energized node reads
// dark; an energized node on a stressed/overloaded corridor takes the CVD-safe danger hue (the same ramp
// the facility parcels + edges use) instead of the neutral asset grey — so EVERY bus (incl. switches /
// transformers / junctions that have no facility parcel) visibly changes under pressure/shortage.
export function nodeAssetStyle(a: AssetSpec): NodeAssetStyle {
  if (!a.lit) return { hex: "#37474f", alpha: 0.35 }; // de-energized / outage → dark + faint (binary)
  if (a.status === "elevated" || a.status === "stressed" || a.status === "shortage") {
    const s = assetConditionStyle(a.status, a.color, 1); // elevated → yellow, stressed → orange, shortage → red
    return { hex: s.hex, alpha: s.alpha };
  }
  return { hex: a.color, alpha: 1 }; // normal / unknown → the neutral asset hue at full opacity
}

export interface NodeAssetLayer {
  apply(assets: readonly AssetSpec[]): void;
}

// Stateful recolour applier over the static node points. `lit` flips rarely, so per tick almost no
// node actually changes style — but the naive version re-ran fromCssColorString (a regex parse) for
// all 1,440 nodes every tick and wrote a fresh Color, which (even though Cesium's color setter guards
// on equality) is pure wasted main-thread work that compounds the freeze. We cache by (a) parsed
// ColorLike per "hex|alpha" key — there are only a handful of distinct styles — and (b) the last style
// key applied to each node id, so an unchanged node is skipped entirely (no parse, no setter call).
export function makeNodeAssetLayer(
  cesium: CesiumLike,
  index: ReadonlyMap<string, PointPrimitiveLike>,
): NodeAssetLayer {
  const colorCache = new Map<string, ColorLike>(); // "hex|alpha" → parsed Color (reused across nodes)
  const lastKey = new Map<string, string>(); // node id → last style key applied (skip when unchanged)

  const colorFor = (key: string, hex: string, alpha: number): ColorLike => {
    let c = colorCache.get(key);
    if (!c) { c = cesium.Color.fromCssColorString(hex).withAlpha(alpha); colorCache.set(key, c); }
    return c;
  };

  return {
    apply(assets): void {
      for (const a of assets) {
        const pt = index.get(a.id);
        if (!pt) continue; // asset may name a node not rendered as a point — skip, not an error
        const s = nodeAssetStyle(a);
        const key = `${s.hex}|${s.alpha}`;
        if (lastKey.get(a.id) === key) continue; // style unchanged since last tick → nothing to do
        pt.color = colorFor(key, s.hex, s.alpha); // Cesium clones into the point's own _color
        lastKey.set(a.id, key);
      }
    },
  };
}
